import { useStyles, Image } from '@archipro-design/aria';
import * as S from './DirectoryPotmTile.style';
import { useRef, useState, useEffect } from 'react';
import { useIsWebmLegacyDevice } from '@modules/root/hook/use-is-webm-legacy-device';
import { useInViewport } from 'ahooks';
import { Link } from '@remix-run/react';
import { useHydrated } from 'remix-utils';
import FilePlayer from 'react-player/file';
import { useLogImpression, useTracker } from '@archipro-website/tracker';

export interface PotmTileProps {
    id: string;
    title: string;
    link?: string;
    professionalID: string;
    professionalName: string;
    videoCoverDesktop: string;
    videoWebm: string;
    videoMp4: string;
    autoplay: boolean;
}

const DirectoryPotmTileDesktop = ({
    id,
    title,
    professionalID,
    professionalName,
    videoWebm,
    videoMp4,
    videoCoverDesktop,
    autoplay,
    link,
}: PotmTileProps) => {
    const tileWidth = 437;
    const tileHeight = 790;
    const [playing, setPlaying] = useState(false);
    const [videoSources, setVideoSources] = useState([videoWebm, videoMp4]);
    const hydrated = useHydrated();
    const { css } = useStyles({ tileWidth, tileHeight, playing });
    const ref = useRef<HTMLAnchorElement>(null);
    const isWebmLegacyDevice = useIsWebmLegacyDevice();
    const [inViewport] = useInViewport(ref, {
        threshold: 0.2,
    });

    useEffect(() => {
        setVideoSources(
            isWebmLegacyDevice ? [videoMp4, videoWebm] : [videoWebm, videoMp4]
        );
    }, [videoMp4, videoWebm, isWebmLegacyDevice]);

    const tracker = useTracker();
    useLogImpression({
        data: {
            professionalID: Number(professionalID),
            itemID: Number(id),
            type: 'Potm_Directory',
        },
        ref,
    });

    //server render only cover image
    const video = hydrated ? (
        <div className={css(S.Player)}>
            <FilePlayer
                url={videoSources}
                playing={autoplay && inViewport}
                muted={true}
                loop={true}
                controls={false}
                playsinline={true}
                width={'100%'}
                height={'100%'}
                onPlay={() => setPlaying(true)}
                config={{
                    attributes: {
                        preload: 'none',
                        poster: videoCoverDesktop,
                    },
                }}
            />
        </div>
    ) : null;

    const trackClick = () => {
        tracker.log('PotmTileClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: JSON.stringify({
                    libraryItemId: id,
                }),
            },
            ga4Data: {
                libraryItemId: id,
            },
        });
    };

    return (
        <Link
            to={link || '#'}
            ref={ref}
            className={css(S.DirectoryGridContainer)}
            prefetch="intent"
            onClick={trackClick}
        >
            <div className={css(S.VideoAspect)}>
                {video}
                <Image
                    className={css(S.Cover)}
                    src={videoCoverDesktop}
                    width={tileWidth}
                    height={tileHeight}
                    layout={'intrinsic'}
                    objectFit={'cover'}
                    objectPosition={'center'}
                    alt={title || ''}
                    styles={{ width: '100%' }}
                />
            </div>
            <div className={css(S.PotmDetails)}>
                <div className="flex flex-col gap-[0.125rem]">
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem]">
                        {title}
                    </span>
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem] text-gray-900/40">
                        {professionalName}
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default DirectoryPotmTileDesktop;
